import { Tooltip, Typography } from 'antd';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { ProductiveHeading06, SingleLineEllipsis } from 'components/styles';
import { useProjectPhaseStatusField } from 'data/fields/hooks/account-fields';
import { ProjectPlanFilter, useProjectPhaseStore } from 'data/projects';
import { Task } from 'data/tasks';

const StyledText = styled(Typography.Text)`
  ${ProductiveHeading06};
  ${SingleLineEllipsis};
  transform: rotate(90deg) translate(-50%, 0);
  transform-origin: top 50%;
  margin-top: 12rem;
  min-width: 12rem;
  max-width: 12rem;
`;

const MinimizedSwimlaneWrapper = styled.div<{
  isClose: boolean;
  index: number;
}>`
  background-color: var(--white-100);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding-top: 1.1rem;
  width: ${(props) => (props.isClose ? '0' : '50px')};
  max-width: ${(props) => (props.isClose ? '0' : '50px')};
  min-width: ${(props) => (props.isClose ? '0' : '50px')};
  overflow-x: hidden;
  margin: 0 var(--spacing-02);
  cursor: pointer;
  margin-left: ${(props) =>
    props.index === 0 ? 'var(--left-spacing)' : 'var(--spacing-02)'};
  transition: all ${(props) => (props.isClose ? '0.1s' : '0.2s')}
    cubic-bezier(0, 0, 0.3, 1);
  box-shadow: var(--shadow-02);

  &:hover {
    box-shadow: var(--shadow-08);
  }
`;

const CompletedMinimizedSwimlane = styled(MinimizedSwimlaneWrapper)`
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

interface MinimizedSwimlaneProps {
  filter: ProjectPlanFilter;
  phaseId: number;
  projectId: number;
  swimlaneState: Record<number, boolean>;
  setSwimlaneState: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  isClose: boolean;
  index: number;
}

export const MinimizedSwimlane: FC<MinimizedSwimlaneProps> = ({
  filter,
  phaseId,
  projectId,
  setSwimlaneState,
  swimlaneState,
  isClose,
  index,
}) => {
  const taskFilterFunction = useCallback(
    (task: Task) =>
      filter.primary.filter(task, filter.primary) &&
      filter.secondary.filter(task, filter.secondary),
    [filter.primary, filter.secondary],
  );
  const { projectPhase } = useProjectPhaseStore(
    projectId,
    phaseId,
    taskFilterFunction,
  );
  const projectPhaseStatusField = useProjectPhaseStatusField();
  const status = projectPhase?.fields.find(
    (f) => f.fieldId === projectPhaseStatusField.fieldId,
  );
  const currentProjectPhaseStatus =
    projectPhaseStatusField.metaData.options.find(
      (_status) => _status.value === status?.fieldValue,
    );

  const SwimlaneWrapper =
    (status?.fieldValue as number) === 3
      ? CompletedMinimizedSwimlane
      : MinimizedSwimlaneWrapper;

  return (
    <>
      <Tooltip title={projectPhase?.projectPhaseName}>
        <SwimlaneWrapper
          isClose={isClose}
          index={index}
          onClick={() => {
            setSwimlaneState({
              ...swimlaneState,
              [phaseId]: false,
            });
          }}
        >
          {currentProjectPhaseStatus ? (
            <StatusIndicator
              showTooltip={false}
              status={currentProjectPhaseStatus}
            />
          ) : null}
          <StyledText>{projectPhase?.projectPhaseName}</StyledText>
        </SwimlaneWrapper>
      </Tooltip>
    </>
  );
};
